import { useDynamicFlag } from '../../../services/launchDarkly';
import type { Game, GameLabel } from '../../../stores/game/types';
import { isPlayerInBattleSegment } from '../../../stores/slot-battle/battleSegmentGate';
import { useStores } from '../../../stores/useStores';

export const useGameLabels = (game: Game, gameLabelFF: boolean) => {
  const { tournamentStore, dailyMissionsStore, coinTypeStore, prizeDropStore, slotBattleStore } = useStores();
  const battleBadge = slotBattleStore.liveBattleBadge;
  // The run's own flag must place the player in the battle's segment, as it must for the widget.
  const battleSegmentFromFlag = useDynamicFlag<string | undefined>(battleBadge?.featureFlagKey, undefined);

  if (!gameLabelFF) {
    return {
      tagGameLabels: [],
      iconGameLabels: [],
    };
  }

  const { coinType: currentCoinType } = coinTypeStore;

  const { gameLabels = [] } = game;

  const { currentTournament, tournamentGameLabel } = tournamentStore;
  const { gcUserDailyMissions, scUserDailyMissions, dailyMissionGameLabel } = dailyMissionsStore;

  const tagGameLabels: GameLabel[] = gameLabels.filter((label) => !label.iconSrc);
  const gameLabelsWithIcons = gameLabels.filter((label) => !!label.iconSrc);
  const iconGameLabels: GameLabel[] = gameLabelsWithIcons;

  const currentDailyMissions = currentCoinType === 'GC' ? gcUserDailyMissions : scUserDailyMissions;
  const isDailyMissionOnGame = currentDailyMissions.some(
    (mission) => mission.status === 'ACTIVE' && mission.gameId === game.masterCasinoGameId,
  );
  const { prizeDropEvent } = prizeDropStore;

  const isPrizeDropOnGame = prizeDropEvent?.eventScopes.participatingGames.some(
    (gameId) => gameId === game.masterCasinoGameId,
  );

  const isPrizeDropOnProvider = prizeDropEvent?.eventScopes.participatingProviders.some(
    (gameId) => gameId === game.masterCasinoProviderId,
  );

  if (currentTournament && tournamentGameLabel) {
    const { tournamentGroup } = currentTournament;

    const isTournamentOnGame =
      (tournamentGroup.gameId && tournamentGroup.gameId === game.identifier) ||
      (tournamentGroup.categoryId &&
        (tournamentGroup.categoryId === game.masterGameSubCategoryId ||
          game.gameCategoryConnections?.some(
            (category) => category.masterGameSubCategoryId === tournamentGroup.categoryId, // For search
          )));

    if (isTournamentOnGame) {
      iconGameLabels.unshift(tournamentGameLabel);
    }
  }

  if (
    battleBadge &&
    isPlayerInBattleSegment(battleSegmentFromFlag, battleBadge.segmentKey) &&
    battleBadge.gameIds.includes(game.masterCasinoGameId)
  ) {
    (battleBadge.label.iconSrc ? iconGameLabels : tagGameLabels).unshift(battleBadge.label);
  }

  if (isDailyMissionOnGame && dailyMissionGameLabel) {
    iconGameLabels.unshift(dailyMissionGameLabel);
  }

  if (prizeDropEvent && (isPrizeDropOnGame || isPrizeDropOnProvider) && prizeDropEvent.gameLabel) {
    iconGameLabels.unshift(prizeDropEvent.gameLabel);
  }

  return {
    tagGameLabels,
    iconGameLabels,
  };
};
